import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loading from "../../Components/Loading";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import { Paginator } from "primereact/paginator";
import CustomDataTable from "../../Components/CustomDataTable";
import { useDateFormatter } from "../../Components/FormatDateTime";
import AudienceCriteriaPillsInline from "./AudienceCriteriaPillsInline";

const AUDIENCES_MOBILE_BREAKPOINT = 1050;

function Import() {
    const { formatDateTime } = useDateFormatter();
    const { t } = useTranslation("common");
    const navigate = useNavigate();

    const [state, setState] = useState({
        importedCustomers: [],
        first: 0,
        count: null,
        size: 15,
        sort: "importedOn,desc",
    });
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= AUDIENCES_MOBILE_BREAKPOINT
    );

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const importedCustomers = useSelector((state) => state.businessCustomers.importedCustomer);
    const importedCustomersCount = useSelector((state) => state.businessCustomers.importedCustomerCount);
    const fetchingImportedCustomers = useSelector((state) => state.businessCustomers.fetchingImportedCustomers);

    const dispatch = useDispatch();
    const getMembership = useCallback(() => dispatch(DefMembershipActions.defMembershipRequest()), [dispatch]);
    const getImportedCustomers = useCallback(
        (businessId, options) => dispatch(BusinessCustomersActions.businessCustomerImportRequest(businessId, options)),
        [dispatch]
    );
    const getImportedCustomersCount = useCallback(
        (businessId) => dispatch(BusinessCustomersActions.businessCustomerImportCountRequest(businessId)),
        [dispatch]
    );

    const fetchAllImportedCustomers = useCallback(
        (page, rows = state.size) => {
            getImportedCustomers(defMembership.business.id, { page, size: rows, sort: state.sort });
        },
        [defMembership?.business?.id, getImportedCustomers, state.size, state.sort]
    );

    useEffect(() => {
        if (!defMembership) {
            getMembership();
            return;
        }
        getImportedCustomersCount(defMembership.business.id);
        fetchAllImportedCustomers(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (importedCustomers) {
            setState((prev) => ({ ...prev, importedCustomers: importedCustomers.content }));
        }
        if (importedCustomersCount) {
            setState((prev) => ({ ...prev, count: importedCustomersCount }));
        }
    }, [importedCustomers, importedCustomersCount]);

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${AUDIENCES_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    if (fetchingImportedCustomers && !state.importedCustomers.length) {
        return <Loading />;
    }

    const handleRowClick = (e) => {
        navigate(`/crm/audiences/audience/${e.data.id}`, { state: { audience: e.data } });
    };

    const onPageChange = (e) => {
        const { page, first, rows } = e;
        fetchAllImportedCustomers(page, rows);
        setState((prevState) => ({
            ...prevState,
            first,
            size: rows,
        }));
    };

    const customerListBody = (data) => (
        <div className="customer-import-id">
            <p className="mb-0 csv-name">{data.importName}</p>
            <p className="import-text records-title importer">{data.externalId}</p>
        </div>
    );

    const customerListDetailsBody = (data) => {
        const importedBy = defMembership?.user?.name;
        const showRecords = (data.entryCount ?? 0) > 0 || data.appliedOn || data.applyStarted;
        return (
            <div className="customer-import-id">
                {showRecords && (
                    <div className="flex align-items-baseline">
                        <p className="mb-0 records-number">{data.entryCount ?? 0}</p>
                        <p className="mb-0 records-title">{t("business_customers.audience_record")}</p>
                    </div>
                )}
                <p className="mb-0 records-date">{formatDateTime(data.importedOn)}</p>
                <p className="m-0 records-title importer">{importedBy}</p>
            </div>
        );
    };

    const membershipCriteriaBodyTemplate = (data) => (
        <AudienceCriteriaPillsInline
            segmentNames={data.segmentNames}
            tagNames={data.tagNames}
            t={t}
            className="audience-list-criteria-cell"
        />
    );

    const audiencesMobileSummaryTemplate = (rowData) => (
        <div className="audiences-mobile-summary">
            <div className="audiences-mobile-section">
                {customerListBody(rowData)}
            </div>
            <div className="audiences-mobile-section">
                {customerListDetailsBody(rowData)}
            </div>
            <div className="audiences-mobile-section audiences-mobile-section-last">
                {membershipCriteriaBodyTemplate(rowData)}
            </div>
        </div>
    );

    const desktopColumns = [
        { field: "externalId", header: t("business_customers.audience_list"), body: customerListBody, alignHeader: "start" },
        { field: "importedOn", header: t("business_customers.audience_list_details"), body: customerListDetailsBody, alignHeader: "start" },
        { field: "membershipCriteria", header: t("business_customers.audience_membership_criteria"), body: membershipCriteriaBodyTemplate, alignHeader: "start" },
    ];

    const mobileSummaryColumn = {
        field: "mobileSummary",
        body: audiencesMobileSummaryTemplate,
        className: "audiences-mobile-summary-column",
    };

    const columnsImports = isMobileStack ? [mobileSummaryColumn] : desktopColumns;

    return (
        <div className="customer-import-screen audiences-list-screen">
            {state.importedCustomers && (
                <div>
                    <CustomDataTable
                        key={`audiences-list-${isMobileStack}`}
                        value={state.importedCustomers}
                        sortField="importedOn"
                        sortOrder={-1}
                        paginator={false}
                        selectionMode="single"
                        responsiveLayout="stack"
                        columns={columnsImports}
                        dataKey="id"
                        onRowClick={handleRowClick}
                        rowClassName={() => "audience-list-row cursor-pointer"}
                    />
                    <Paginator
                        rows={state.size}
                        totalRecords={state.count}
                        first={state.first}
                        onPageChange={onPageChange}
                        className="custom-paginator"
                    />
                </div>
            )}
        </div>
    );
}

export default Import;
